import React from "react";
import { useQuery } from "react-query";

export interface CareerSearchResultEntry {
	url?: string;
	title?: string;
	minEducation?: number|null;
	maxEducation?: number|null;
	educationUnit?: string|null;
	minSalary?: number|null;
	maxSalary?: number|null;
	region?: string[]
}

type CareerSearchQueryKey = [string, string, number, number];

async function fetchSearch({queryKey} : {queryKey: CareerSearchQueryKey}) : Promise<CareerSearchResultEntry[]> {
	const [, keywords, maxEducationDuration, minCareerSalary] = queryKey;
	const url = new URL('https://' + location.hostname  + '/wp-json/wi-health-careers-search/v1/search');
	url.searchParams.append('keywords', keywords.toString());
	url.searchParams.append('maxEducationDuration', maxEducationDuration.toString());
	url.searchParams.append('minCareerSalary', minCareerSalary.toString());
	const response = await fetch(url, {
		method: 'GET',
		credentials: 'same-origin',
	});
	if (response.ok)
		return response.json();
	throw new Error(response.statusText);
}

export default function useCareerSearchQuery(keywords: string, maxEducationDuration: number, minCareerSalary: number) {
	return useQuery<CareerSearchResultEntry[], Error, CareerSearchResultEntry[], CareerSearchQueryKey>([
		"career-search", keywords.toLowerCase(), maxEducationDuration, minCareerSalary
	], fetchSearch, {
		enabled: !!keywords || minCareerSalary > 0 || !!maxEducationDuration, // If we have searchQuery, then enable the query on render
		retry: 5, //retry 5 times before erroring
		retryDelay: 1000 //retry every second
	});
}

export function useFieldsDebounce(keywords: string, maxEducationDuration: number, minCareerSalary, delay: number = 500) {
	const [debouncedKeywords, setDebouncedKeywords] = React.useState(keywords);
	const [debouncedMaxEducationDuration, setDebouncedMaxEducationDuration] = React.useState(maxEducationDuration);
	const [debouncedMinCareerSalary, setDebouncedMinCareerSalary] = React.useState(minCareerSalary);

	React.useEffect(() => {
		const handler: NodeJS.Timeout = setTimeout(() => {
			if (keywords != debouncedKeywords)
				setDebouncedKeywords(keywords);
			if (maxEducationDuration != debouncedMaxEducationDuration)
				setDebouncedMaxEducationDuration(maxEducationDuration);
			if (minCareerSalary != debouncedMinCareerSalary)
				setDebouncedMinCareerSalary(minCareerSalary);
		}, delay);

		// Cancel the timeout if value changes (also on delay change or unmount)
		return () => {
			clearTimeout(handler);
		};
	}, [keywords, maxEducationDuration, minCareerSalary, delay]);

	return [debouncedKeywords, debouncedMaxEducationDuration, debouncedMinCareerSalary];
}
